import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Benefits.module.css'; // Adjust the path as necessary
import { useDispatch, useSelector } from 'react-redux';
import { Veriff } from '@veriff/js-sdk';
import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk';
import BasicPopup from '../../UI/Popups/BasicPopup/BasicPopup';

import BubbleButton from '../../UI/Buttons/BubbleButton/BubbleButton';
import { updateUser } from '../../../services/userService';
import {
    CheckIcon,
    JobAccess,
    NotificationBell,
    ProfileInsight,
    ProfilePictureShowHide,
    ProfileVisible,
    RecommendProfile,
    SendMessage,
    UploadMediaCV
} from '../../../assets/images/icons';
import { updateVerificationStatus } from '../../../services/verificationService';
import toast from 'react-hot-toast';
import { fetchUserByToken } from '../../../app/features/userSlice';

function Benefits() {
    const { t } = useTranslation();
    const [isOpenVerificationPopup, setIsOpenVerificationPopup] = useState(false);
    const language = useSelector((state) => state.translation.language);
    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch()

    const benefitsData = [
        {
            key: 'profileVerificationBadge',
            icon: CheckIcon,
            name: t('benefits.profileVerificationBadge.name'),
            description: t('benefits.profileVerificationBadge.description')
        },
        {
            key: 'instantNewJobNotification',
            icon: NotificationBell,
            name: t('benefits.instantNewJobNotification.name'),
            description: t('benefits.instantNewJobNotification.description')
        },
        {
            key: 'sendMessageToEmployer',
            icon: SendMessage,
            name: t('benefits.sendMessageToEmployer.name'),
            description: t('benefits.sendMessageToEmployer.description')
        },
        {
            key: 'uploadMultipleCVs',
            icon: UploadMediaCV,
            name: t('benefits.uploadMultipleCVs.name'),
            description: t('benefits.uploadMultipleCVs.description')
        },
        {
            key: 'recommendedProfile',
            icon: RecommendProfile,
            name: t('benefits.recommendedProfile.name'),
            description: t('benefits.recommendedProfile.description')
        },
        {
            key: 'exclusiveJobsAccess',
            icon: JobAccess,
            name: t('benefits.exclusiveJobsAccess.name'),
            description: t('benefits.exclusiveJobsAccess.description')
        },
        {
            key: 'increasedProfileVisibility',
            icon: ProfileVisible,
            name: t('benefits.increasedProfileVisibility.name'),
            description: t('benefits.increasedProfileVisibility.description')
        },
        {
            key: 'showHideProfilePicture',
            icon: ProfileInsight,
            name: t('benefits.showHideProfilePicture.name'),
            description: t('benefits.showHideProfilePicture.description')
        },
        {
            key: 'profileInsights',
            icon: ProfilePictureShowHide,
            name: t('benefits.profileInsights.name'),
            description: t('benefits.profileInsights.description')
        }
    ];

    useEffect(() => {
        if (user.verification_payment_done && user.verification_status !== 'approved') {
            setIsOpenVerificationPopup(true)
        }
    }, [user.verification_payment_done])

    useEffect(() => {
        if (isOpenVerificationPopup) {
            if (user.verification_payment_done && user.verification_status !== 'approved') {
                const veriff = new Veriff({
                    apiKey: process.env.REACT_APP_VERIFF_API_KEY,
                    parentId: 'veriff-root',
                    onSession: (err, response) => {
                        const formData = new FormData();
                        formData.append('token', userToken);
                        formData.append('verification_id', response.verification.id);
                        updateUser(formData);
                        createVeriffFrame({
                            url: response.verification.url,
                            lang: language,
                            onEvent: async function (msg) {
                                console.log(msg, 'msg STARTED');
                                switch (msg) {
                                    case MESSAGES.STARTED:
                                        console.log('STARTED');
                                        break;
                                    case MESSAGES.FINISHED:
                                        console.log('FINISHED')

                                        toast.promise(
                                            updateVerificationStatus(response.verification.id, userToken), {
                                            loading: t('general.UI.loading'),
                                            success: <b>{t('forms.welcome_employer.profile_successfully_verified')}</b>,
                                            error: (err) => {
                                                return <b>{t('general.UI.something_went_wrong')}</b>;
                                            },
                                        }).then(() => {
                                            if (userToken) {
                                                setTimeout(() => {
                                                    dispatch(fetchUserByToken(userToken));
                                                    setIsOpenVerificationPopup(false)
                                                }, 1000);
                                            }
                                        })
                                            .catch((error) => {
                                                toast.error(error.error);
                                            });
                                        break;
                                    case MESSAGES.CANCELED:
                                        console.log('CANCELED');
                                        break;
                                    default:
                                        console.log('DEFAULT');
                                        break;
                                }
                            }
                        });

                    },
                });

                veriff.setParams({
                    person: {
                        givenName: ' ',
                        lastName: ' '
                    },
                    vendorData: ' '
                });

                veriff.mount({
                    submitBtnText: t('benefits.verifyButtonText')
                });

            }
        }
    }, [isOpenVerificationPopup, user, language, userToken, t,])

    return (
        <div className={styles.container}>
            <div className={styles.fcvRow} style={{ display: "flex", flexDirection: "column", justifyContent: 'center', alignItems: "center" }}>
                <p className={styles.benefitsTitle}>{t('benefits.benefitsTitle')}</p>
                <div className={styles.grid_container}>
                    {benefitsData.map((benefit, index) => (
                        <div key={index} className={styles.grid_item}>
                            <div className={styles.image_container}>
                                <img
                                    src={benefit.icon}
                                    alt={benefit.name}
                                    className={styles.image}
                                />
                            </div>
                            <div className={styles.text_container}>
                                <div className={styles.name}>{benefit.name}</div>
                                <div className={styles.description}>{benefit.description}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <BasicPopup
                isOpen={isOpenVerificationPopup}
                closePopup={() => {
                    // setIsOpenVerificationPopup(false)
                }}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px'
                }}>
                    <h2 style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#093559',
                        margin: '0'
                    }}>
                        {t('benefits.verificationTitle')}
                    </h2>
                    <p style={{
                        fontSize: '14px',
                        color: '#666',
                        textAlign: 'center',
                        margin: '0 0 10px 0'
                    }}>
                        {t('benefits.verificationDescription')}
                    </p>
                    <div className={styles.verifyButton}>
                        <div className={styles.VerificationProcess}>
                            {user.verification_payment_done && <div id="veriff-root" />}
                        </div>
                    </div>
                </div>
            </BasicPopup>
        </div>
    );
}

export default Benefits;
